import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';

import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import { Viewport } from 'components/ViewportProvider';
import DeliveryDateTime from 'containers/DeliveryDateTime';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { DeliveryDatetime } from 'types/schema';
import ModalNote from '../ProductNoteModal';
import AddIcon from '../icons/addIcon';
import NoteIcon from '../icons/noteIcon';

const CardWrapper = styled.div`
  padding: 12px 0px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: stretch;
  border-radius: 8px;
  box-shadow: 0 4px 24px 0 rgba(58, 85, 9, 0.04);
  margin: 0px 8px;

  ${media.lg`
    padding: 20px 0px;
    gap: 16px;
    margin: 0;
  `}
`;

const HeadTitle = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #111827;
  font-weight: 600;
  padding: 0px 16px;

  ${media.lg`
    padding: 0px 20px;
    font-size: 20px;
    line-height: 28px;
    font-weight: 700;
  `}
`;

const ItemTitle = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #6b7280;
  font-weight: 500;
`;

const ItemValue = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: #111827;
  font-weight: 400;
`;

const DeliveryInfoLayout = styled.div`
  padding: 0px 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 12px;
  row-gap: 12px;

  ${media.lg`
    grid-template-columns: 1fr 1fr 1fr 1fr;
    padding: 0px 20px;

  `};
`;

const DeliveryDateTimeContainer = styled.div`
  grid-column: span 2;
`;

const DeliveryAddressWrapper = styled.div`
  grid-column: span 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #f2f3f5;
`;

const DeliveryNoteWrapper = styled.div`
  padding: 0px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${media.lg`
      padding: 0px 20px;
  `};
`;

const DeliveryNotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AddDeliveryNoteButton = styled(Button)`
  padding: 0 !important;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  width: fit-content;
`;

const AddDeliveryNoteText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #3c9041;
`;

interface DeliveryInformationProps {
  singleSupplierCart: any;
  checkout: any;
  draftOrderId: string;
  deliveryDatetime: DeliveryDatetime;
  onChangeDeliveryDateTime: (data: { draftOrderId: string; supplierId: number; start: string; end: string }) => void;
  onUpdateDeliveryNotes: (data: { draftOrderId: string; supplierId: number; notes: string[] }) => void;
  onAddNote: (data: { draftOrderId: string; supplierId: number; note: string }) => void;

  isOpenCalendar: boolean;
  setOpenCalendar: (open: boolean) => void;

  deliveryAddress?: string;
}

const DeliveryInformation = (props: DeliveryInformationProps) => {
  const {
    checkout,
    singleSupplierCart,
    draftOrderId,
    deliveryDatetime,
    onChangeDeliveryDateTime,
    onUpdateDeliveryNotes,
    onAddNote,
    deliveryAddress,
    isOpenCalendar,
    setOpenCalendar,
  } = props;

  const [isOpenNoteModal, onOpenNoteModal] = useState(false);
  const [reuseNote, onReuseNote] = useState(false);

  const specificNotes = useMemo(() => {
    const specificNotes: string[] = [];
    checkout?.selectedDeliveryNotes[singleSupplierCart?.supplier?.id]?.forEach((dn: string) => {
      const note = checkout?.deliveryNotes.find((n) => n.text === dn);
      if (!note) {
        specificNotes.push(dn);
      }
    });
    return specificNotes;
  }, [checkout?.deliveryNotes, checkout?.selectedDeliveryNotes, singleSupplierCart?.supplier?.id]);

  // handlers
  const onChangeDate = useCallback(
    (date: any) => {
      const newStartDateValue = moment(date).date();
      const newStartMonthValue = moment(date).month();
      const newStartYearValue = moment(date).year();

      const updatedStartDatetime = moment(deliveryDatetime.start as string)
        .year(newStartYearValue)
        .month(newStartMonthValue)
        .date(newStartDateValue)
        .toISOString();

      const updatedEndDatetime = moment(deliveryDatetime.end as string)
        .year(newStartYearValue)
        .month(newStartMonthValue)
        .date(newStartDateValue)
        .toISOString();

      onChangeDeliveryDateTime({
        draftOrderId: draftOrderId,
        supplierId: singleSupplierCart?.supplier.id,
        start: updatedStartDatetime,
        end: updatedEndDatetime,
      });
    },
    [draftOrderId, onChangeDeliveryDateTime, singleSupplierCart],
  );

  const onChangeTime = useCallback(
    (time: string) => {
      // Time có dạng: 14:30 - 15:30
      const startTime = time?.split(' - ')[0];
      const endTime = time?.split(' - ')[1];

      const newStartHour = startTime?.split(':')[0];
      const newStartMinute = startTime?.split(':')[1];
      const newEndHour = endTime.split(':')[0];
      const newEndMinute = endTime.split(':')[1];

      const updatedStartDatetime = moment(deliveryDatetime.start as string)
        .hour(Number(newStartHour))
        .minute(Number(newStartMinute))
        .toISOString();
      const updatedEndDatetime = moment(deliveryDatetime.end as string)
        .hour(Number(newEndHour))
        .minute(Number(newEndMinute))
        .toISOString();

      onChangeDeliveryDateTime({
        draftOrderId: draftOrderId,
        supplierId: singleSupplierCart?.supplier.id,
        start: updatedStartDatetime,
        end: updatedEndDatetime,
      });
    },
    [deliveryDatetime, draftOrderId, onChangeDeliveryDateTime, singleSupplierCart],
  );

  const onCheckDeliveryNote = useCallback(
    (text: string) => (event: any) => {
      const newDeliveryNotes = [...(checkout?.selectedDeliveryNotes[singleSupplierCart?.supplier?.id] || [])];
      if (event.target.checked) {
        newDeliveryNotes.push(text);
      } else {
        newDeliveryNotes.splice(newDeliveryNotes.indexOf(text), 1);
      }
      onUpdateDeliveryNotes({
        draftOrderId: draftOrderId,
        supplierId: singleSupplierCart?.supplier?.id,
        notes: newDeliveryNotes,
      });
    },
    [checkout?.selectedDeliveryNotes, draftOrderId, onUpdateDeliveryNotes, singleSupplierCart],
  );

  const onSubmitAddNote = useCallback(
    (text: any) => {
      const trimmedText = text?.trim();

      const matchedNote = checkout?.deliveryNotes?.find((n) => n.text === trimmedText);

      if (specificNotes?.includes(trimmedText) || matchedNote) {
        if (matchedNote) {
          const selectedNotes = checkout?.selectedDeliveryNotes[singleSupplierCart?.supplier?.id] || [];

          if (!selectedNotes.includes(trimmedText)) {
            onUpdateDeliveryNotes({
              draftOrderId: draftOrderId,
              supplierId: singleSupplierCart?.supplier?.id,
              notes: [...selectedNotes, trimmedText],
            });
          }
        }

        onOpenNoteModal(false);
        return;
      }

      if (reuseNote) {
        // onAddNote handles both createDeliveryNote and setDraftOrderDeliveryNote internally
        onAddNote({
          draftOrderId: draftOrderId,
          supplierId: singleSupplierCart?.supplier?.id,
          note: text,
        });
      } else {
        // Only update selected notes for this draft order (temp note, not saved globally)
        onUpdateDeliveryNotes({
          supplierId: singleSupplierCart?.supplier?.id,
          notes: [...(checkout?.selectedDeliveryNotes[singleSupplierCart?.supplier?.id] || []), text],
          draftOrderId: draftOrderId,
        });
      }
      onOpenNoteModal(false);
    },
    [
      checkout?.selectedDeliveryNotes,
      checkout?.deliveryNotes,
      draftOrderId,
      onUpdateDeliveryNotes,
      onAddNote,
      singleSupplierCart,
      reuseNote,
    ],
  );

  return (
    <Viewport.Consumer>
      {({ width }) => {
        const isDesktopSize = width >= 1024;
        return (
          <CardWrapper>
            <HeadTitle>
              <FormattedMessage {...utilsMessages.deliveryInformation} />
            </HeadTitle>
            {!isDesktopSize && <Divider />}
            {!!singleSupplierCart?.supplier.id && !!checkout.deliveryDate[singleSupplierCart?.supplier.id] && (
              <DeliveryInfoLayout>
                <DeliveryAddressWrapper>
                  <ItemTitle>
                    <FormattedMessage {...utilsMessages.deliveryAddress} />
                  </ItemTitle>
                  <ItemValue>{deliveryAddress}</ItemValue>
                </DeliveryAddressWrapper>
                <DeliveryDateTimeContainer>
                  <DeliveryDateTime
                    supplierId={singleSupplierCart?.supplier.id}
                    deliveryDate={checkout.deliveryDate[singleSupplierCart?.supplier.id]}
                    deliveryTime={checkout.timeSlot[singleSupplierCart?.supplier?.id]}
                    onDeliveryDateChange={onChangeDate}
                    onDeliveryTimeChange={onChangeTime}
                    isOpenCalendar={isOpenCalendar}
                    setOpenCalendar={setOpenCalendar}
                  />
                </DeliveryDateTimeContainer>
              </DeliveryInfoLayout>
            )}
            <DeliveryNoteWrapper>
              {(!!checkout?.deliveryNotes?.length || !!specificNotes?.length) && (
                <ItemTitle>
                  <FormattedMessage {...utilsMessages.noteForDelivery} />
                </ItemTitle>
              )}
              <DeliveryNotesList>
                {checkout?.deliveryNotes?.map((item: any) => (
                  <div key={item.id}>
                    <Checkbox
                      checked={
                        checkout?.selectedDeliveryNotes[singleSupplierCart?.supplier?.id]?.indexOf(item.text) > -1
                      }
                      onChange={onCheckDeliveryNote(item.text)}
                    >
                      {item.text}
                    </Checkbox>
                  </div>
                ))}
                {specificNotes?.map((text: any) => (
                  <Checkbox key={text} checked={true} onChange={onCheckDeliveryNote(text)}>
                    {text}
                  </Checkbox>
                ))}

                {!checkout?.deliveryNotes?.length && !specificNotes?.length ? (
                  <AddDeliveryNoteButton
                    type="link"
                    size="small"
                    onClick={() => {
                      onOpenNoteModal(true);
                    }}
                  >
                    <NoteIcon color="#3C9041" />
                    <AddDeliveryNoteText>
                      <FormattedMessage {...utilsMessages.noteForDelivery} />
                    </AddDeliveryNoteText>
                  </AddDeliveryNoteButton>
                ) : (
                  <AddDeliveryNoteButton
                    type="link"
                    size="small"
                    onClick={() => {
                      onOpenNoteModal(true);
                    }}
                  >
                    <AddIcon />
                    <AddDeliveryNoteText>
                      <FormattedMessage {...utilsMessages.addNote} />
                    </AddDeliveryNoteText>
                  </AddDeliveryNoteButton>
                )}
              </DeliveryNotesList>
            </DeliveryNoteWrapper>
            <ModalNote
              headerLabel={<FormattedMessage {...utilsMessages.noteForDelivery} />}
              isOpen={isOpenNoteModal}
              onSubmit={onSubmitAddNote}
              onClose={() => onOpenNoteModal(false)}
              note={''}
            >
              <Checkbox checked={reuseNote} onChange={() => onReuseNote(!reuseNote)}>
                <FormattedMessage {...utilsMessages.saveDeliveryNote} />
              </Checkbox>
            </ModalNote>
          </CardWrapper>
        );
      }}
    </Viewport.Consumer>
  );
};

export default DeliveryInformation;
